import type { CoachingBreathworkOutcome } from './constants';

/**
 * Guided breathing (SC-30): the built-in patterns and the timing maths the
 * session screen runs on.
 *
 * The maths is a pure function of ELAPSED time on purpose. A timer that counts
 * its own ticks drifts every time the browser throttles a background tab or a
 * phone locks, and the member ends up breathing to a clock that has stopped
 * agreeing with the ring. The screen stores when the session started and asks
 * `breathPositionAt` where that puts them, so a late tick simply lands later
 * in the same session.
 */

/** Seconds for each part of one breath. A zero hold is skipped. */
export type BreathworkPattern = {
  inhale: number;
  holdIn: number;
  exhale: number;
  holdOut: number;
};

export const BREATH_PHASES = ['inhale', 'holdIn', 'exhale', 'holdOut'] as const;
export type BreathPhase = (typeof BREATH_PHASES)[number];

/** What the ring says during each phase. */
export const BREATH_PHASE_CUES: Record<BreathPhase, string> = {
  inhale: 'Breathe in',
  holdIn: 'Hold',
  exhale: 'Breathe out',
  holdOut: 'Hold',
};

/** Bounds an admin-written pattern must stay inside. */
export const BREATHWORK_LIMITS = { phaseMaxSeconds: 20, cyclesMax: 40 } as const;

export const BUILT_IN_BREATHWORK_KEYS = [
  'box',
  'coherent',
  'long-exhale',
  'four-seven-eight',
  'energizing',
  'grounding',
  'intention',
] as const;
export type BuiltInBreathworkKey = (typeof BUILT_IN_BREATHWORK_KEYS)[number];

export type BuiltInBreathwork = {
  key: BuiltInBreathworkKey;
  title: string;
  description: string;
  outcome: CoachingBreathworkOutcome;
  pattern: BreathworkPattern;
  cycles: number;
};

/**
 * The patterns every tenant starts with. They ship in code so a fix reaches
 * everyone; an admin row with the same key overrides one (or hides it), and a
 * keyless admin row adds a pattern of the tenant's own.
 */
export const BUILT_IN_BREATHWORK: readonly BuiltInBreathwork[] = [
  {
    key: 'box',
    title: 'Box breathing',
    description:
      'Four equal counts to breathe in, hold, breathe out and hold. A steady rhythm for settling distractions before focused work.',
    outcome: 'focus',
    pattern: { inhale: 4, holdIn: 4, exhale: 4, holdOut: 4 },
    cycles: 15,
  },
  {
    key: 'coherent',
    title: 'Coherent breathing',
    description:
      'Slow, even breaths of five counts in and five counts out, for a calm and balanced state.',
    outcome: 'calm',
    pattern: { inhale: 5, holdIn: 0, exhale: 5, holdOut: 0 },
    cycles: 18,
  },
  {
    key: 'long-exhale',
    title: 'Long exhale',
    description: 'An exhale twice as long as the inhale, to slow down after a demanding moment.',
    outcome: 'calm',
    pattern: { inhale: 4, holdIn: 0, exhale: 8, holdOut: 0 },
    cycles: 10,
  },
  {
    key: 'four-seven-eight',
    title: '4-7-8 breathing',
    description:
      'Breathe in for four, hold for seven and breathe out for eight. A wind-down practice for the end of the day.',
    outcome: 'sleep',
    pattern: { inhale: 4, holdIn: 7, exhale: 8, holdOut: 0 },
    cycles: 4,
  },
  {
    key: 'energizing',
    title: 'Energizing breath',
    description:
      'A full inhale and a brisk exhale to lift your energy. Breathe gently, and stop if you feel lightheaded.',
    outcome: 'energy',
    pattern: { inhale: 4, holdIn: 0, exhale: 2, holdOut: 0 },
    cycles: 12,
  },
  {
    key: 'grounding',
    title: 'Grounding breath',
    description:
      'A steady inhale, a short pause and a slow exhale to steady yourself before a conversation that matters.',
    outcome: 'confidence',
    pattern: { inhale: 4, holdIn: 2, exhale: 6, holdOut: 0 },
    cycles: 12,
  },
  {
    key: 'intention',
    title: 'Intention breath',
    description:
      'Even breaths with a pause at the top and the bottom. Hold your goal in mind during each pause.',
    outcome: 'manifestation',
    pattern: { inhale: 5, holdIn: 3, exhale: 5, holdOut: 3 },
    cycles: 10,
  },
];

export function isBuiltInBreathworkKey(
  key: string | null | undefined,
): key is BuiltInBreathworkKey {
  return (BUILT_IN_BREATHWORK_KEYS as readonly string[]).includes(key ?? '');
}

/** Seconds in one full breath. */
export function breathCycleSeconds(p: BreathworkPattern): number {
  return BREATH_PHASES.reduce((sum, phase) => sum + Math.max(0, p[phase]), 0);
}

/** Seconds in a whole session of `cycles` breaths. */
export function breathSessionSeconds(p: BreathworkPattern, cycles: number): number {
  return breathCycleSeconds(p) * Math.max(0, cycles);
}

/** `4-7-8`, `4-4-4-4`, `5-5`: the conventional short name of a pattern. */
export function breathPatternLabel(p: BreathworkPattern): string {
  if (p.holdIn === 0 && p.holdOut === 0) return `${p.inhale}-${p.exhale}`;
  if (p.holdOut === 0) return `${p.inhale}-${p.holdIn}-${p.exhale}`;
  return `${p.inhale}-${p.holdIn}-${p.exhale}-${p.holdOut}`;
}

export type BreathPosition = {
  done: boolean;
  /** 1-based round, never above `cycles`. */
  cycle: number;
  phase: BreathPhase;
  /** Milliseconds into the current phase, and that phase's length. */
  phaseElapsedMs: number;
  phaseMs: number;
  elapsedMs: number;
  totalMs: number;
};

/** The last phase of a breath that actually lasts, so a finished session still names one. */
function lastPhase(p: BreathworkPattern): BreathPhase {
  return [...BREATH_PHASES].reverse().find((phase) => p[phase] > 0) ?? 'exhale';
}

/**
 * Where `elapsedMs` into a session of `cycles` breaths puts the member. Zero
 * holds are skipped, a negative elapsed time is the very start, and anything
 * past the end is `done` on the final phase.
 */
export function breathPositionAt(
  p: BreathworkPattern,
  cycles: number,
  elapsedMs: number,
): BreathPosition {
  const cycleMs = breathCycleSeconds(p) * 1000;
  const rounds = Math.max(1, Math.floor(cycles));
  const totalMs = cycleMs * rounds;
  const final = lastPhase(p);
  const finishedAt = (): BreathPosition => ({
    done: true,
    cycle: rounds,
    phase: final,
    phaseElapsedMs: p[final] * 1000,
    phaseMs: p[final] * 1000,
    elapsedMs: totalMs,
    totalMs,
  });
  if (cycleMs <= 0) return finishedAt();

  const at = Math.max(0, elapsedMs);
  if (at >= totalMs) return finishedAt();
  const index = Math.floor(at / cycleMs);
  let into = at - index * cycleMs;
  for (const phase of BREATH_PHASES) {
    const ms = p[phase] * 1000;
    if (ms <= 0) continue;
    if (into < ms) {
      return {
        done: false,
        cycle: index + 1,
        phase,
        phaseElapsedMs: into,
        phaseMs: ms,
        elapsedMs: at,
        totalMs,
      };
    }
    into -= ms;
  }
  return finishedAt();
}
